import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, isRespAdmin, RUOLI_LABEL } from "@/context/AuthContext";
import api from "@/lib/api";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { AppLogo, sbloccaInterfaccia } from "@/components/Shared";
import PullToRefresh, { triggerAppRefresh } from "@/components/PullToRefresh";
import { pushSupportato, eIscritto } from "@/lib/pushNotifiche";
import { useRealtimeSync } from "@/lib/realtime";
import {
  LayoutDashboard, Inbox, Users, Contact, Flag, MapPin, Gamepad2, Trophy,
  CalendarDays, ClipboardCheck, Tent, Settings, ScrollText, LogOut,
  MoreHorizontal, Target, Megaphone, UserCircle, Users2, ClipboardList, ShieldCheck,
  Bell, Medal, Instagram, UserCheck,
} from "lucide-react";

/** Firma dell'autore. L'icona è quella del bundle (lucide): nessuna risorsa
 *  esterna, quindi non tocca la Content Security Policy. */
function FirmaAutore({ className = "" }) {
  return (
    <a
      href="https://www.instagram.com/_luca_marrone_"
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-amber-600 transition-colors ${className}`}
      data-testid="firma-autore"
    >
      created by <Instagram className="h-3.5 w-3.5" /> _luca_marrone_
    </a>
  );
}

// Promemoria mostrati nella campanella dell'header (profilo, notifiche, bacheca)
function useAvvisi(pathname) {
  const [avvisi, setAvvisi] = useState([]);
  useEffect(() => {
    // Segna la bacheca come "vista" quando la si apre
    if (pathname === "/bacheca") {
      try { localStorage.setItem("bacheca_visto", new Date().toISOString()); } catch { /* noop */ }
    }
    let vivo = true;
    const ricarica = async () => {
      const out = [];
      try {
        const { data } = await api.get("/profilo");
        const importanti = ["telefono", "taglia_maglia", "data_nascita"];
        if (importanti.some((k) => data.bloccati && !data.bloccati[k])) {
          out.push({ testo: "Completa il tuo profilo", to: "/profilo" });
        }
      } catch { /* noop */ }
      try {
        if (pushSupportato() && !(await eIscritto())) out.push({ testo: "Attiva le notifiche", to: "/profilo" });
      } catch { /* noop */ }
      try {
        const { data } = await api.get("/annunci");
        const ultimo = data[0]?.created_at;
        const visto = (() => { try { return localStorage.getItem("bacheca_visto") || ""; } catch { return ""; } })();
        if (ultimo && ultimo > visto) out.push({ testo: "Nuovo avviso in Bacheca", to: "/bacheca" });
      } catch { /* noop */ }
      if (vivo) setAvvisi(out);
    };
    ricarica();
    // Anche la campanella si aggiorna in tempo reale (es. nuovo annuncio)
    window.addEventListener("app:refresh", ricarica);
    return () => { vivo = false; window.removeEventListener("app:refresh", ricarica); };
  }, [pathname]);
  return avvisi;
}

// Dropdown che si chiude a un click ovunque (listener globale, non un fondale:
// il backdrop-blur dell'header confinerebbe un fixed inset-0 alla sola barra).
function useChiudiFuori() {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  return { ref, open, setOpen };
}

function Campanella({ avvisi, testId }) {
  const { ref, open, setOpen } = useChiudiFuori();
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="relative text-slate-400 p-2 rounded-full hover:bg-slate-100 transition-colors" aria-label="Avvisi" data-testid={testId || "header-campanella"}>
        <Bell className="h-5 w-5" />
        {avvisi.length > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-2xl bg-white shadow-xl border border-slate-100 p-1.5 z-50" data-testid="campanella-dropdown">
          {avvisi.length === 0 ? (
            <p className="text-xs text-slate-400 font-semibold text-center py-3">Nessun avviso</p>
          ) : avvisi.map((a, i) => (
            <NavLink key={i} to={a.to} onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid={`avviso-${i}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" /> {a.testo}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ user, onLogout }) {
  const { ref, open, setOpen } = useChiudiFuori();
  const iniziali = (((user.nome || "")[0] || "") + ((user.cognome || "")[0] || "")).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="h-9 w-9 rounded-full bg-amber-500 text-white font-display font-extrabold text-sm flex items-center justify-center shadow-sm hover:bg-amber-600 transition-colors" aria-label="Menu utente" data-testid="header-avatar">
        {iniziali}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 rounded-2xl bg-white shadow-xl border border-slate-100 p-1.5 z-50" data-testid="avatar-menu">
          <div className="px-3 py-2 border-b border-slate-50 mb-1">
            <p className="text-sm font-bold text-slate-800 truncate">{user.nome} {user.cognome}</p>
            <p className="text-[11px] text-slate-400 font-semibold">{RUOLI_LABEL[user.role]}</p>
          </div>
          <NavLink to="/profilo" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid="avatar-profilo">
            <UserCircle className="h-4 w-4" /> Profilo
          </NavLink>
          <button type="button" onClick={() => { setOpen(false); onLogout(); }} className="w-full flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-red-50 text-sm font-semibold text-red-500" data-testid="avatar-esci">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      )}
    </div>
  );
}

export const NAV = [
  { to: "/inizio", label: "Home", icon: LayoutDashboard, roles: ["animatore", "aiuto_animatore"] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "responsabile"] },
  { to: "/segreteria", label: "Segreteria", icon: Inbox, roles: ["admin", "responsabile"] },
  { to: "/animati", label: "Animati", icon: Contact, roles: ["admin", "responsabile"] },
  { to: "/animatori", label: "Animatori", icon: Users, roles: ["admin", "responsabile"] },
  { to: "/responsabili", label: "Responsabili", icon: UserCheck, roles: ["admin"] },
  { to: "/account-genitori", label: "Account Genitori", icon: Users2, roles: ["admin"] },
  { to: "/programma", label: "Programma", icon: ClipboardList, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/gruppi", label: "Gruppi Attività", icon: Users2, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/squadre", label: "Squadre", icon: Flag, roles: ["admin", "responsabile"] },
  { to: "/postazioni", label: "Postazioni", icon: MapPin, roles: ["admin", "responsabile"] },
  { to: "/mia-postazione", label: "Postazione", icon: Target, roles: ["animatore", "aiuto_animatore"] },
  { to: "/bacheca", label: "Bacheca", icon: Megaphone, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/giochi", label: "Giochi", icon: Gamepad2, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/classifica", label: "Classifica", icon: Trophy, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/presenze", label: "Presenze", icon: ClipboardCheck, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
  { to: "/camposcuola", label: "Camposcuola", icon: Tent, roles: ["admin", "responsabile"] },
  { to: "/gestione-punti", label: "Gestione Punti", icon: Medal, roles: ["admin", "responsabile"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin", "responsabile"] },
  { to: "/registro", label: "Registro Attività", icon: ScrollText, roles: ["admin"] },
  { to: "/informativa-privacy", label: "Informativa Privacy", icon: ShieldCheck, roles: ["admin"] },
  { to: "/diritti", label: "Diritti interessati", icon: UserCheck, roles: ["admin", "responsabile"] },
  { to: "/profilo", label: "Profilo", icon: UserCircle, roles: ["admin", "responsabile", "animatore", "aiuto_animatore"] },
];

const MOBILE_MAIN = {
  admin: ["/dashboard", "/animati", "/squadre", "/calendario"],
  responsabile: ["/dashboard", "/animati", "/squadre", "/calendario"],
  // Animatori e aiuti: Home come prima voce, poi le sezioni quotidiane
  animatore: ["/inizio", "/programma", "/mia-postazione", "/presenze"],
  aiuto_animatore: ["/inizio", "/programma", "/mia-postazione", "/presenze"],
};

const STAFF_OPERATIONAL_PATHS = new Set(["/programma", "/presenze", "/mia-postazione"]);

const MENU_SECTIONS = [
  { key: "space", title: "Il mio spazio", paths: ["/calendario", "/bacheca"] },
  { key: "users", title: "Utenti", paths: ["/segreteria", "/responsabili", "/account-genitori", "/animatori", "/animati"] },
  { key: "points", title: "Punti & classifica", paths: ["/classifica", "/gestione-punti"] },
  { key: "oratorio", title: "Oratorio", paths: ["/squadre", "/postazioni", "/giochi", "/gruppi", "/programma", "/presenze", "/mia-postazione"] },
  { key: "camposcuola", title: "Camposcuola", paths: ["/camposcuola"] },
  { key: "admin", title: "Amministrazione", paths: ["/impostazioni", "/diritti", "/registro", "/informativa-privacy"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Rete di sicurezza: se un dialog ha lasciato la pagina non cliccabile,
  // cambiando sezione l'interfaccia torna comunque utilizzabile.
  useEffect(() => { sbloccaInterfaccia(); }, [location.pathname]);

  // La classifica è nascosta finché non è pubblica: la vedono nel menu solo
  // admin e il Responsabile designato (lo stato arriva dal server).
  const [vedeClassifica, setVedeClassifica] = useState(user.role === "admin");
  const [gestiscePunti, setGestiscePunti] = useState(user.role === "admin");
  useEffect(() => {
    let vivo = true;
    const ricarica = () => {
      api.get("/classifica/stato").then((r) => {
        if (!vivo) return;
        setVedeClassifica(!!r.data.puoi_vedere);
        setGestiscePunti(!!r.data.puoi_gestire_punti);
      }).catch(() => {});
    };
    ricarica();
    // In tempo reale: la voce Classifica appare/sparisce appena viene pubblicata
    window.addEventListener("app:refresh", ricarica);
    return () => { vivo = false; window.removeEventListener("app:refresh", ricarica); };
  }, [location.pathname]);

  const [giornataOperativa, setGiornataOperativa] = useState(null);
  useEffect(() => {
    let vivo = true;
    const ricarica = () => {
      api.get("/operativita/stato").then((r) => {
        if (vivo) setGiornataOperativa(r.data);
      }).catch(() => {
        if (vivo) setGiornataOperativa({ attiva: false });
      });
    };
    ricarica();
    window.addEventListener("app:refresh", ricarica);
    return () => { vivo = false; window.removeEventListener("app:refresh", ricarica); };
  }, []);

  // Connessione realtime unica per tutta l'app: ogni segnale dal server
  // produce un "app:refresh" (debounced) che ricarica i dati della pagina
  // corrente, della campanella e del gating del menu.
  useRealtimeSync();

  const staffOperativo = isRespAdmin(user) || giornataOperativa?.attiva === true;
  const items = NAV.filter((n) => n.roles.includes(user.role)
    && (n.to !== "/classifica" || vedeClassifica)
    && (n.to !== "/gestione-punti" || gestiscePunti)
    && (isRespAdmin(user) || !STAFF_OPERATIONAL_PATHS.has(n.to) || staffOperativo));

  // Barra in basso: preferenza personale dell'utente (max 5) se impostata,
  // altrimenti il default per ruolo. "Altro" resta sempre fisso a destra.
  const preferite = Array.isArray(user.nav_preferita) ? user.nav_preferita : null;
  let mobileMain;
  if (preferite && preferite.length) {
    mobileMain = preferite.map((p) => items.find((n) => n.to === p)).filter(Boolean).slice(0, 5);
  } else {
    const mainPaths = MOBILE_MAIN[user.role] || [];
    mobileMain = items.filter((n) => mainPaths.includes(n.to));
  }
  const mainSet = new Set(mobileMain.map((n) => n.to));
  const groupedSections = MENU_SECTIONS.map((section) => ({
    ...section,
    items: section.paths.map((path) => items.find((n) => n.to === path)).filter(Boolean),
  })).filter((section) => section.items.length > 0);
  const mobileMore = groupedSections.flatMap((section) => section.items).filter((n) => !mainSet.has(n.to));

  const avvisi = useAvvisi(location.pathname);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-dvh bg-[#FFFDF9]">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-72 flex-col bg-white border-r border-slate-100 z-30">
        <div className="flex items-center gap-3 px-5 py-5">
          <AppLogo size={44} className="shrink-0 drop-shadow-md" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-slate-900 leading-tight">SS. Medici</p>
            <p className="text-xs text-slate-400 font-semibold">Oratorio Estivo</p>
          </div>
          <Campanella avvisi={avvisi} testId="campanella-desktop" />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
          <div>
            <p className="px-3 mb-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">Home</p>
            {items.filter((n) => n.to === "/dashboard" || n.to === "/inizio").map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                    isActive ? "bg-amber-500 text-white shadow-sm" : "text-slate-600 hover:bg-amber-50 hover:text-amber-700"
                  }`
                }
              >
                <n.icon className="h-[18px] w-[18px]" />
                {n.label}
              </NavLink>
            ))}
          </div>
          {groupedSections.map((section) => (
            <div key={section.key}>
              <p className="px-3 mb-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">{section.title}</p>
              <div className="space-y-0.5">
                {section.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.to.slice(1)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                        isActive ? "bg-amber-500 text-white shadow-sm" : "text-slate-600 hover:bg-amber-50 hover:text-amber-700"
                      }`
                    }
                  >
                    <n.icon className="h-[18px] w-[18px]" />
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-4">
          <NavLink to="/profilo" className="block rounded-2xl px-2 py-1.5 hover:bg-slate-50 transition-colors mb-2" data-testid="sidebar-profilo">
            <p className="text-sm font-bold text-slate-800 truncate">{user.nome} {user.cognome}</p>
            <p className="text-xs text-slate-400">{RUOLI_LABEL[user.role]} · Vedi profilo</p>
          </NavLink>
          <button onClick={doLogout} data-testid="logout-btn" className="btn-ghost w-full !py-2 text-xs">
            <LogOut className="h-4 w-4" /> Esci
          </button>
          <FirmaAutore className="mt-3" />
        </div>
      </aside>

      {/* Header mobile */}
      <header className="md:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-100 flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <div className="flex items-center gap-2.5 min-w-0">
          <AppLogo size={34} className="shrink-0 drop-shadow-sm" />
          <p className="font-display font-extrabold text-slate-900 text-sm truncate">SS. Medici Oratorio</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Campanella avvisi={avvisi} testId="campanella-mobile" />
          <Avatar user={user} onLogout={doLogout} />
        </div>
      </header>

      {/* Contenuto */}
      <main className="md:ml-72 px-4 md:px-8 py-6 pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-10 max-w-6xl">
        <PullToRefresh onRefresh={triggerAppRefresh}>
          {children}
        </PullToRefresh>
      </main>

      {/* Bottom nav mobile */}
      {/* Bottom nav flottante stile "liquid glass": pillola staccata dai bordi,
          blur + saturazione del contenuto che scorre dietro, voce attiva evidenziata */}
      <nav className="md:hidden fixed z-30 inset-x-4 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] mx-auto max-w-md rounded-[2.25rem] bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border border-white/60 shadow-[0_8px_32px_rgba(15,23,42,0.14)] flex justify-around items-center px-1.5 py-1.5">
        {mobileMain.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`bottomnav-${n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-2xl text-[10px] font-bold transition-all min-w-0 ${
                isActive ? "bg-amber-500/15 text-amber-600" : "text-slate-400"
              }`
            }
          >
            <n.icon className="h-5 w-5 shrink-0" />
            <span className="max-w-[52px] truncate">{n.label.split(" ")[0]}</span>
          </NavLink>
        ))}
        {mobileMore.length > 0 && (
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="bottomnav-altro"
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-2xl text-[10px] font-bold text-slate-400 min-w-0"
          >
            <MoreHorizontal className="h-5 w-5" />
            Altro
          </button>
        )}
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent className="rounded-t-3xl bg-white">
          <div className="mx-auto w-full max-w-md px-5 pb-[calc(2rem+env(safe-area-inset-bottom))] pt-2">
            <DrawerTitle className="font-display font-extrabold text-slate-900 mb-4 text-center">Tutte le sezioni</DrawerTitle>
            <div className="space-y-5 max-h-[65dvh] overflow-y-auto pb-2">
              {groupedSections.map((section) => {
                const sectionItems = section.items.filter((n) => !mainSet.has(n.to));
                if (sectionItems.length === 0 && !(section.key === "points" && user.role === "admin")) return null;
                return (
                  <section key={section.key}>
                    <h3 className="mb-2 px-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">{section.title}</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {sectionItems.map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          onClick={() => setMoreOpen(false)}
                          data-testid={`morenav-${n.to.slice(1)}`}
                          className={`flex items-center gap-2 rounded-3xl bg-slate-50 p-4 text-xs font-bold text-slate-600 hover:bg-amber-50 hover:text-amber-700 transition-colors ${n.to === "/segreteria" ? "col-span-3 justify-start" : "flex-col"}`}
                        >
                          <n.icon className="h-6 w-6" />
                          <span className="text-center leading-tight">{n.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
            <FirmaAutore className="mt-5" />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
